import React, { type VideoHTMLAttributes } from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';

interface PlayerOptions extends VideoHTMLAttributes<HTMLVideoElement> {
  sources?: Array<{
    src: string,
    type?: string
  }>,
  aspectRatio?: string,
  responsive?: boolean,
  fluid?: boolean
}

interface VideoPlayerProps {
  options: PlayerOptions,
  onReady?: (player: Player) => void
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const videoRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<Player | null>(null);
  const { options, onReady } = props;

  React.useEffect(() => {

    // Make sure Video.js player is only initialized once
    if (!playerRef.current && videoRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode. 
      const videoElement = document.createElement("video-js");

      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = playerRef.current = videojs(videoElement, options, () => {
        videojs.log('player is ready');
        onReady && onReady(player);
      });

      player.muted(options.muted ?? true);
      player.autoplay(options.autoPlay ?? true);
      player.src(options.sources);

      player.on('error', () => {
        const error = player.error();
        if (error) {
          console.error('Video.js Error:', error.code, error.message);
          // Implement custom error handling logic here
          // e.g., display a user-friendly message, retry stream, etc.
        }
      });

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else if (playerRef.current) {
      //const player = playerRef.current;

    }
  }, [options, videoRef, playerRef, onReady]);

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div data-vjs-player>
      <div ref={videoRef} />
    </div>
  );
}

export default VideoPlayer;